import { useRef, useState, useEffect } from 'react';
import { saveUser, resetUserPassword, updateUserWallet } from '../../../services/UsersService';
import SelectLimit from './SelectLimit';
import SwitchInput from '../../../components/SwitchInput';

function formatVaultUsd(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0.00';
    const prefix = number > 0 ? '+' : '';
    return `${prefix}${number.toFixed(2)}`;
}

/**
 * props:
 * - data
 * - onSubmit
 */
function UserModal(props) {

    const DEFAULT_USER = {
        name: '',
        email: '',
        password: '',
        limitId: 0,
        telegramChat: '',
        phone: '',
        isActive: false,
        isCustody: false,
        custodyUsdc: '',
        pixKey: ''
    }

    const [error, setError] = useState('');
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [copyOk, setCopyOk] = useState(false);

    const [user, setUser] = useState(DEFAULT_USER);
    const dataRef = useRef(props.data);

    const btnClose = useRef('');
    const btnSave = useRef('');

    dataRef.current = props.data;

    function applyUserData(data) {
        const source = data || {};
        setGeneratedPassword('');
        setCopyOk(false);
        setUser({
            ...DEFAULT_USER,
            ...source,
            isCustody: !!source.isCustody,
            custodyUsdc: source.tradableUsdc !== null && source.tradableUsdc !== undefined
                ? source.tradableUsdc
                : (source.custodyUsdc !== null && source.custodyUsdc !== undefined ? source.custodyUsdc : ''),
            pixKey: source.pixKey || '',
            walletStopUsdc: source.walletStopUsdc != null ? source.walletStopUsdc : '',
            walletTotal: source.walletTotal,
            walletStopHitAt: source.walletStopHitAt || null
        });
    }

    function finishAndClose(result) {
        btnClose.current.click();
        if (props.onSubmit) props.onSubmit(result);
    }

    function onSubmit(event) {
        const payload = {
            name: user.name,
            email: user.email,
            phone: user.phone,
            telegramChat: user.telegramChat,
            pixKey: user.pixKey,
            limitId: user.limitId,
            isActive: user.isActive,
            isCustody: user.isCustody,
            walletStopUsdc: user.walletStopUsdc === '' || user.walletStopUsdc == null
                ? null
                : user.walletStopUsdc
        };
        if (user.password)
            payload.password = user.password;

        let walletPatch = null;
        if (payload.isCustody) {
            const parsed = user.custodyUsdc === '' || user.custodyUsdc === null || user.custodyUsdc === undefined
                ? undefined
                : parseFloat(user.custodyUsdc);
            if (!user.id) {
                if (parsed !== undefined)
                    payload.custodyUsdc = parsed;
            } else if (parsed !== undefined) {
                const original = parseFloat(
                    props.data?.tradableUsdc !== undefined && props.data?.tradableUsdc !== null
                        ? props.data.tradableUsdc
                        : props.data?.custodyUsdc
                );
                if (!(Number.isFinite(parsed) && Number.isFinite(original) && parsed === original))
                    walletPatch = { tradableUsdc: parsed };
            }
        }

        const afterSave = result => {
            if (result.generatedPassword) {
                setGeneratedPassword(result.generatedPassword);
                setError('');
                setUser(prev => ({ ...prev, id: result.id, ...result }));
                return;
            }
            finishAndClose(result);
        };

        saveUser(user.id, payload)
            .then(result => {
                if (!walletPatch) return afterSave(result);
                return updateUserWallet(result.id || user.id, walletPatch).then(() => afterSave(result));
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? err.response.data : err.message);
            })
    }

    function onResetClick(event) {
        if (!window.confirm('Gerar nova senha para este usuário? A senha atual será invalidada.'))
            return;

        resetUserPassword(user.id)
            .then(result => {
                if (result.generatedPassword) {
                    setGeneratedPassword(result.generatedPassword);
                    setError('');
                    return;
                }
                finishAndClose(result);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? err.response.data : err.message);
            })
    }

    function onCopyPassword() {
        if (!generatedPassword) return;
        navigator.clipboard.writeText(generatedPassword).then(() => {
            setCopyOk(true);
            setTimeout(() => setCopyOk(false), 2000);
        }).catch(() => setError('Não foi possível copiar a senha.'));
    }

    function onInputChange(event) {
        setUser(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    useEffect(() => {
        applyUserData(props.data);
    }, [props.data?.id])

    useEffect(() => {
        const modal = document.getElementById('modalUser');
        if (!modal) return undefined;

        function onShown(event) {
            const trigger = event?.relatedTarget;
            if (trigger && trigger.id === 'btnNewUser') {
                applyUserData(DEFAULT_USER);
                return;
            }
            applyUserData(dataRef.current);
        }

        function onHidden() {
            setUser({ ...DEFAULT_USER });
            setError('');
            setGeneratedPassword('');
            setCopyOk(false);
        }

        modal.addEventListener('shown.bs.modal', onShown);
        modal.addEventListener('hidden.bs.modal', onHidden);
        return () => {
            modal.removeEventListener('shown.bs.modal', onShown);
            modal.removeEventListener('hidden.bs.modal', onHidden);
        };
    }, [])

    return (
        <div className="modal fade" id="modalUser" tabIndex="-1" role="dialog" aria-labelledby="modalTitleNotify" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="modalTitleNotify">{props.data?.id ? 'Edit ' : 'New '}User</p>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>
                    <div className="modal-body">
                        {
                            generatedPassword
                                ? (
                                    <div className="alert alert-success">
                                        <div className="fw-bold mb-2">Senha de login (copie agora — não poderá ser vista depois):</div>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <code className="fs-5 user-select-all">{generatedPassword}</code>
                                            <button type="button" className="btn btn-sm btn-outline-success" onClick={onCopyPassword}>
                                                {copyOk ? 'Copiado!' : 'Copiar'}
                                            </button>
                                        </div>
                                        <small className="d-block mt-2 text-muted">Email: {user.email}</small>
                                    </div>
                                )
                                : <></>
                        }
                        <div className="form-group">
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="name">Name:</label>
                                        <input type="text" id="name" className="form-control" onChange={onInputChange} value={user.name || ""} placeholder="User's full name" />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="email">Email:</label>
                                        <input type="text" id="email" className="form-control" onChange={onInputChange} value={user.email || ""} placeholder="user@email.com" />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="phone">Cellphone:</label>
                                        <input type="text" id="phone" className="form-control" onChange={onInputChange} value={user.phone || ""} placeholder="+5551123456789" />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="telegramChat">Telegram Chat ID: <a className="badge bg-secondary py-1" href="/telegram-chat" target="_blank">?</a></label>
                                        <input type="text" id="telegramChat" className="form-control" onChange={onInputChange} value={user.telegramChat || ""} placeholder="123456789" />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="pixKey">Chave PIX:</label>
                                        <input type="text" id="pixKey" className="form-control" onChange={onInputChange} value={user.pixKey || ""} placeholder="CPF, e-mail, telefone ou chave aleatória" maxLength={120} />
                                        <small className="text-muted">Usada no saque de custódia: o saldo em USDC é convertido para BRL e enviado a esta chave.</small>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="limit">Limit:</label>
                                        <SelectLimit id={user.limitId} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <SwitchInput id="isActive" text="Is Active?" onChange={onInputChange} isChecked={user.isActive || false} />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <SwitchInput id="isCustody" text="Usuário de Custódia?" onChange={onInputChange} isChecked={user.isCustody || false} />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="walletStopUsdc">Stop-loss de carteira (USDC)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            id="walletStopUsdc"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={user.walletStopUsdc === 0 || user.walletStopUsdc ? user.walletStopUsdc : ''}
                                            placeholder="Vazio desliga"
                                        />
                                        <small className="text-muted d-block">
                                            Se a carteira (USDC + moedas) cair a este valor, vende tudo, encerra os monitores e dolariza.
                                            {user.walletTotal != null
                                                ? ` Saldo atual: ${Number(user.walletTotal).toFixed(2)} USDC.`
                                                : ''}
                                        </small>
                                        {user.walletStopHitAt
                                            ? (
                                                <small className="text-warning d-block mt-1">
                                                    Disparou em {new Date(user.walletStopHitAt).toLocaleString('pt-BR')}. Altere o valor para rearmar.
                                                </small>
                                            )
                                            : null}
                                    </div>
                                </div>
                            </div>
                            {
                                user.isCustody
                                    ? (
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group">
                                                    <label htmlFor="custodyUsdc">Saldo livre (USDC para comprar)</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        id="custodyUsdc"
                                                        className="form-control"
                                                        onChange={onInputChange}
                                                        value={user.custodyUsdc === 0 || user.custodyUsdc ? user.custodyUsdc : ''}
                                                        placeholder="Ex: 50"
                                                    />
                                                    <small className="text-muted">Este valor é o USDC da carteira, incluindo lucros já realizados. Entra nas compras.</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group">
                                                    <label>PnL</label>
                                                    <div className="form-control bg-light">
                                                        {formatVaultUsd(user.pnl != null ? user.pnl : user.custodyVault?.net)} USDC
                                                    </div>
                                                    <small className="text-muted d-block">
                                                        Versus {Number(user.depositedUsdc != null ? user.depositedUsdc : 0).toFixed(2)} USDC depositado
                                                        {' · '}
                                                        Realizado {Number(user.custodyVault?.gained || 0).toFixed(2)} / {Number(user.custodyVault?.lost || 0).toFixed(2)}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                    : <></>
                            }
                        </div>
                    </div>
                    <div className="modal-footer">
                        {
                            error
                                ? <div className="alert alert-danger mt-1 col-9 py-1">{error}</div>
                                : <></>
                        }
                        {
                            generatedPassword
                                ? <button type="button" className="btn btn-sm btn-primary" onClick={() => finishAndClose(user)}>Fechar</button>
                                : (
                                    <>
                                        {
                                            user.id > 0
                                                ? <button type="button" className="btn btn-sm btn-secondary" onClick={onResetClick}>Gerar nova senha</button>
                                                : <></>
                                        }
                                        <button ref={btnSave} type="button" className="btn btn-sm btn-primary" onClick={onSubmit}>Save</button>
                                    </>
                                )
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UserModal;
